import { type Editor, Node, mergeAttributes } from '@tiptap/react';

import { mentionQueryAt } from '@/products/intranet/features/intranet/comment-mention';

/**
 * Inline @-mention. Stored as `<span data-user-id="…">@Name</span>`: the id is what the server
 * reads; the name is only the visible text, so no second attribute has to survive sanitising.
 */
export const IntranetMention = Node.create({
  name: 'mention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: false,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-user-id'),
        renderHTML: (attributes: { id: string | null }) =>
          attributes.id ? { 'data-user-id': attributes.id } : {},
      },
      label: {
        default: '',
        parseHTML: (element) => (element.textContent ?? '').replace(/^@/, ''),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-user-id]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), `@${String(node.attrs.label)}`];
  },

  renderText({ node }) {
    return `@${String(node.attrs.label)}`;
  },
});

export type MentionTrigger = { query: string; from: number; to: number };

/** The @-token right before an empty selection, with its document range. */
export function mentionTriggerAt(editor: Editor): MentionTrigger | null {
  const { selection } = editor.state;
  if (!selection.empty) {
    return null;
  }
  const { $from } = selection;
  const before = $from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc');
  const query = mentionQueryAt(before, before.length);
  if (query === null) {
    return null;
  }
  return { query, from: $from.pos - query.length - 1, to: $from.pos };
}

export function insertMentionNode(
  editor: Editor,
  trigger: MentionTrigger,
  colleague: { id: string; displayName: string },
): void {
  editor
    .chain()
    .focus()
    .insertContentAt({ from: trigger.from, to: trigger.to }, [
      { type: 'mention', attrs: { id: colleague.id, label: colleague.displayName } },
      { type: 'text', text: ' ' },
    ])
    .run();
}
